import { Component } from '@theme/component';
import { MegaMenuHoverEvent } from '@theme/events';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleDocumentKeydown);
    this.addEventListener('click', this.#handleSidebarClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleDocumentKeydown);
    this.removeEventListener('click', this.#handleSidebarClick);
    this.#setSidebarState(false);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#activateItem(item);
  };

  /**
   * Toggle a submenu with click. Menu items without submenu remain regular links.
   * @param {MouseEvent} event
   */
  toggleByClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[ref="submenu[]"]')) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    // const menuItemContainer = item.closest('[role="presentation"]');
    const menuItemContainer = item.closest('.menu-list__list-item');
    const isMoreTrigger = Boolean(event.target.closest('[slot="more"]'));
    const isOverflowItem = menuItemContainer?.getAttribute('slot') === 'overflow';
    const hasSubmenu = Boolean(findSubmenu(item));

    // Keep plain links clickable. Only "More" and items with submenu should toggle.
    if (!isMoreTrigger && !hasSubmenu) {
      if (isOverflowItem) {
        this.#deactivate();
      }
      return;
    }

    event.preventDefault();

    const isAlreadyExpanded = this.#state.activeItem === item && item.ariaExpanded === 'true';
    if (isAlreadyExpanded) {
      this.#deactivate(item);
      return;
    }

    this.#activateItem(item);
  };

  /**
   * Close sidebar-style mega menu.
   * @param {MouseEvent} event
   */
  closeSidebar = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#deactivate();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.#state.activeItem) return;

    const submenu = findSubmenu(this.#state.activeItem);
    const submenuInner = submenu?.querySelector('.menu-list__submenu-inner');
    const clickedInsideInner = submenuInner?.contains(event.target) ?? false;

    if (clickedInsideInner) return;

    const clickedInsideSubmenuWrapper = submenu?.contains(event.target) ?? false;
    // Click on overlay area (inside submenu wrapper but outside visible panel) should close.
    if (clickedInsideSubmenuWrapper) {
      this.#deactivate();
      return;
    }

    if (this.contains(event.target)) return;
    this.#deactivate();
  };

  #handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.#deactivate();
  };

  #handleSidebarClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-mega-sidebar-close]')) {
      this.closeSidebar(event);
      return;
    }

    const nestedSummary = event.target.closest('.mega-menu__nested-summary');
    if (nestedSummary instanceof HTMLElement && this.contains(nestedSummary)) {
      const nestedDetails = nestedSummary.closest('.mega-menu__nested-details');
      if (!(nestedDetails instanceof HTMLDetailsElement)) return;

      event.preventDefault();
      event.stopPropagation();

      const nestedToggle = event.target.closest('[data-mega-nested-toggle]');
      const isKeyboardActivation = event.detail === 0;

      if (nestedToggle || isKeyboardActivation) {
        if (nestedDetails.hasAttribute('open')) {
          nestedDetails.removeAttribute('open');
          this.#resetNestedSubtree(nestedDetails);
        } else {
          nestedDetails.setAttribute('open', '');
        }
        return;
      }

      const nestedUrl = nestedSummary.dataset.megaNestedUrl;
      if (nestedUrl) {
        window.location.href = nestedUrl;
      }
      return;
    }

    const nestedBackTrigger = event.target.closest('[data-mega-nested-back]');
    if (nestedBackTrigger instanceof HTMLElement && this.contains(nestedBackTrigger)) {
      event.preventDefault();
      event.stopPropagation();

      const nestedList = nestedBackTrigger.closest('.mega-menu__nested-list');
      const nestedDetails = nestedList?.parentElement;
      if (nestedDetails instanceof HTMLDetailsElement) {
        nestedDetails.removeAttribute('open');
        this.#resetNestedSubtree(nestedDetails);
      }
      return;
    }

    const trigger = event.target.closest('[data-mega-sidebar-trigger]');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    const panelId = trigger.dataset.megaSidebarPanel;
    if (!panelId) return;

    const isAlreadyActive = trigger.getAttribute('data-mega-sidebar-active') === 'true';
    if (isAlreadyActive) return;

    event.preventDefault();
    this.#setActiveSidebarPanel(trigger, panelId);
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.#setSidebarState(false);
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    this.#resetNestedSubtree(submenu);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Activate a top-level menu item and calculate open menu height.
   * @param {HTMLElement} item
   */
  #activateItem(item) {
    if (!this.headerComponent || item == this.#state.activeItem) return;

    // const menuItemContainer = item.closest('[role="presentation"]');
    const menuItemContainer = item.closest('.menu-list__list-item');
    const isDefaultSlot = menuItemContainer?.getAttribute('slot') !== 'overflow';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);
    const isSidebarSubmenu = Boolean(submenu?.querySelector('[data-mega-sidebar="true"]'));

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    this.#setSidebarState(isSidebarSubmenu);

    if (submenu) {
      submenu.dataset.active = '';
      this.#initializeSidebarInSubmenu(submenu);
      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    if (isSidebarSubmenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Toggle global UI state for sidebar mega menu mode.
   * @param {boolean} isOpen
   */
  #setSidebarState(isOpen) {
    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle('mega-menu-sidebar-open', isOpen);
    body?.classList.toggle('mega-menu-sidebar-open', isOpen);

    if (this.headerComponent) {
      if (isOpen) {
        this.headerComponent.setAttribute('data-sidebar-menu-open', 'true');
      } else {
        this.headerComponent.removeAttribute('data-sidebar-menu-open');
      }
    }
  }

  /**
   * Ensure sidebar-mode mega menus have one active category and matching right-side panel.
   * @param {HTMLElement} submenu
   */
  #initializeSidebarInSubmenu(submenu) {
    const sidebarMenus = submenu.querySelectorAll('[data-mega-sidebar="true"]');
    sidebarMenus.forEach((sidebarMenu) => {
      const activeTrigger =
        sidebarMenu.querySelector('[data-mega-sidebar-trigger][data-mega-sidebar-active="true"]') ||
        sidebarMenu.querySelector('[data-mega-sidebar-trigger]');

      if (!(activeTrigger instanceof HTMLElement)) return;

      const panelId = activeTrigger.dataset.megaSidebarPanel;
      if (!panelId) return;

      this.#setActiveSidebarPanel(activeTrigger, panelId, sidebarMenu);
    });
  }

  /**
   * Toggle active state for a sidebar-style mega menu.
   * @param {HTMLElement} trigger
   * @param {string} panelId
   * @param {ParentNode} [scope]
   */
  #setActiveSidebarPanel(trigger, panelId, scope = trigger.closest('[data-mega-sidebar="true"]')) {
    if (!scope) return;

    const triggers = scope.querySelectorAll('[data-mega-sidebar-trigger]');
    const panels = scope.querySelectorAll('[data-mega-sidebar-panel-content]');
    const promos = scope.querySelectorAll('[data-mega-sidebar-promo-content]');
    const promoId = trigger.dataset.megaSidebarPromo;

    triggers.forEach((item) => {
      const isActive = item === trigger;
      item.classList.toggle('mega-menu__sidebar-trigger--active', isActive);
      item.setAttribute('data-mega-sidebar-active', isActive ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      const panelElement = /** @type {HTMLElement} */ (panel);
      const isActive = panelElement.id === panelId;
      panelElement.classList.toggle('mega-menu__sidebar-panel--active', isActive);
      if (!isActive) {
        this.#resetNestedSubtree(panelElement);
      }
      panelElement.hidden = !isActive;
    });

    promos.forEach((promo) => {
      const promoElement = /** @type {HTMLElement} */ (promo);
      const isActive = promoId && promoElement.id === promoId;
      promoElement.hidden = !isActive;
    });
  }

  /**
   * Close all opened nested submenu levels in the provided scope.
   * @param {ParentNode | null | undefined} scope
   */
  #resetNestedSubtree(scope) {
    if (!scope) return;
    const openNestedDetails = scope.querySelectorAll('.mega-menu__nested-details[open]');
    openNestedDetails.forEach((item) => item.removeAttribute('open'));
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.closest('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.closest('[slot="more"]')?.parentElement?.querySelector('[slot="overflow"]'));
  }

  const menuItem = element.closest('[ref="menuitem"]') || element.querySelector('[ref="menuitem"]');
  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
